import { Link, useParams, Navigate } from "react-router-dom";
import { Users, BedDouble, Bath, Check, ArrowRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { useLang } from "../i18n";

export default function CameraDettaglio() {
  const { slug } = useParams();
  const { l, data, t } = useLang();
  const { SITE, ROOMS } = data;
  const d = t.roomDetail;
  const room = ROOMS.find((r) => r.slug === slug);
  if (!room) return <Navigate to={l("/camere")} replace />;
  const others = ROOMS.filter((r) => r.slug !== slug);

  return (
    <>
      <Seo
        title={d.seoTitle(room.name, room.type, room.capacity)}
        description={d.seoDescription(room.name, room.beds, room.capacity, room.excerpt)}
        path={`/camere/${room.slug}`}
      />
      <section className="max-w-7xl mx-auto px-5 md:px-10 pt-32 md:pt-44 pb-12">
        <Reveal>
          <nav aria-label="Breadcrumb" className="text-xs uppercase tracking-[0.18em] text-body mb-6">
            <Link to={l("/camere")} className="hover:text-terra transition-colors duration-300">{d.breadcrumb}</Link>
            <span className="mx-2 text-sand">/</span>
            <span className="text-ink">{room.name}</span>
          </nav>
          <p className="eyebrow mb-4">{room.type}</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight text-ink max-w-3xl">
            {room.name}
          </h1>
          <p className="mt-6 text-base md:text-lg text-body leading-relaxed max-w-2xl">
            {room.excerpt}
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 pb-16">
        <Reveal className="md:col-span-8">
          <div className="img-frame aspect-[16/10]">
            <img src={room.gallery[0]} alt={room.imageAlt} className="w-full h-full object-cover" />
          </div>
        </Reveal>
        <div className="md:col-span-4 grid grid-rows-2 gap-6 md:gap-8">
          {room.gallery.slice(1).map((img, i) => (
            <Reveal key={i} delay={0.08 * (i + 1)} className="h-full">
              <div className="img-frame h-full">
                <img src={img} alt={d.detailAlt(room.name)} loading="lazy" className="w-full h-full object-cover" />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-10 pb-20 md:pb-28 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-7">
          <Reveal>
            <h2 className="font-serif text-2xl md:text-3xl text-ink">{d.theRoom}</h2>
          </Reveal>
          {room.description.map((p, i) => (
            <Reveal key={i} delay={0.06 * i}>
              <p className="mt-5 text-sm md:text-base text-body leading-relaxed">{p}</p>
            </Reveal>
          ))}
          <Reveal delay={0.15}>
            <h2 className="font-serif text-2xl md:text-3xl text-ink mt-12">{d.servicesTitle}</h2>
            <ul className="mt-6 space-y-3">
              {room.services.map((s) => (
                <li key={s} className="flex items-start gap-3 text-sm md:text-base text-body">
                  <Check className="w-4 h-4 mt-1 text-terra shrink-0" aria-hidden="true" />
                  {s}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="md:col-span-4 md:col-start-9">
          <aside data-testid="room-facts-panel" className="border border-sand bg-surface p-8 sticky top-28">
            <h2 className="font-serif text-xl text-ink">{d.summary}</h2>
            <ul className="mt-5 space-y-4 text-sm text-body">
              <li className="flex gap-3">
                <Users className="w-4 h-4 mt-0.5 text-terra shrink-0" aria-hidden="true" />
                <span>{d.maxCapacity} <strong className="text-ink font-semibold">{room.capacity} {d.people}</strong></span>
              </li>
              <li className="flex gap-3">
                <BedDouble className="w-4 h-4 mt-0.5 text-terra shrink-0" aria-hidden="true" />
                <span>{room.beds}</span>
              </li>
              <li className="flex gap-3">
                <Bath className="w-4 h-4 mt-0.5 text-terra shrink-0" aria-hidden="true" />
                <span>{d.privateBathroom}</span>
              </li>
            </ul>
            <p className="mt-5 text-sm text-body border-t border-sand pt-5">
              {d.recommendedFor} <span className="text-ink">{room.idealFor}</span>
            </p>
            <a
              href={SITE.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="room-booking-button"
              className="mt-7 w-full inline-flex items-center justify-center rounded-full bg-terra px-6 py-4 text-sm font-semibold text-cream transition-[background-color,transform] duration-300 hover:bg-terra-dark hover:-translate-y-0.5"
            >
              {d.book}
            </a>
            <Link
              to={l("/contatti")}
              data-testid="room-contact-link"
              className="mt-3 w-full inline-flex items-center justify-center rounded-full border border-sage px-6 py-4 text-sm font-medium text-sage transition-colors duration-300 hover:bg-sage hover:text-cream"
            >
              {d.request}
            </Link>
          </aside>
        </Reveal>
      </section>

      <section className="border-t border-sand bg-surface">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24">
          <Reveal>
            <h2 className="font-serif text-2xl md:text-4xl text-ink">{d.others}</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {others.map((r, i) => (
              <Reveal key={r.slug} delay={0.07 * i}>
                <Link
                  to={l(`/camere/${r.slug}`)}
                  data-testid={`other-room-link-${r.slug}`}
                  className="group block border border-sand bg-cream p-6 transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-[0_20px_40px_-24px_rgba(26,26,26,0.3)]"
                >
                  <p className="text-xs uppercase tracking-[0.18em] text-terra">{r.type}</p>
                  <p className="font-serif text-xl text-ink mt-2 group-hover:text-sage transition-colors duration-300">{r.name}</p>
                  <p className="text-sm text-body mt-1">{d.upTo} {r.capacity} {d.guests}</p>
                  <p className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-sage">
                    {d.see} <ArrowRight className="w-4 h-4" />
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
